<script setup>
import CFDomainSelect from '#modules/domains/components/CFDomainSelect.vue';
import CFDomainSidebarLink from '#modules/domains/components/CFDomainSidebarLink.vue';
import { usePermissions } from '#modules/auth/composables/usePermissions.js';
import CFRoundedButton from '@/ui/RoundedButton/CFRoundedButton.vue';
import CFSidebar from '@/shared/components/CFSidebar.vue';
import { useDomain } from '#modules/domains/queries/useDomain.js';
import { AppPermissions } from '#modules/auth/constants/permissions.js';
import { computed } from 'vue';

const { can } = usePermissions();
const { domainId, domain, isPending, isError, isActive } = useDomain();

const backRoute = computed(() =>
    window.history.state?.back?.startsWith('/zones') ? { name: 'zones' } : { name: 'domains' }
);
</script>

<template>
    <CFSidebar>
        <div class="px-8">
            <RouterLink
                :to="backRoute"
                class="mb-10 flex items-center pt-9"
            >
                <CFRoundedButton
                    icon="arrow-left"
                    color="secondary"
                    class="me-2"
                />
                {{ $t('general.back_to_overview') }}
            </RouterLink>

            <CFDomainSelect
                v-if="domainId && !isError"
                :current="domain"
            />

            <div
                v-if="domainId"
                class="mt-5"
            >
                <CFDomainSidebarLink
                    :label="$t('general.overview')"
                    icon="layout-dashboard"
                    :route="{ name: 'single domain', params: { domain: domainId } }"
                />
            </div>

            <template v-if="!isPending && domainId && (isActive || can(AppPermissions.ManageDomains))">
                <hr class="my-5" />
                <p class="text-xs leading-3 font-bold">{{ $t('general.settings') }}</p>
                <div class="mt-5 flex flex-col space-y-4">
                    <CFDomainSidebarLink
                        v-if="isActive"
                        :label="$t('domain.nameservers')"
                        icon="network-settings"
                        :route="{ name: 'domain nameservers', params: { domain: domainId } }"
                    />

                    <CFDomainSidebarLink
                        v-if="can(AppPermissions.ManageDomains)"
                        :label="$t('domain.dns_records')"
                        icon="app-window-expand"
                        :route="{ name: 'domain dns', params: { domain: domainId } }"
                    />

                    <CFDomainSidebarLink
                        v-if="can(AppPermissions.ManageDomains)"
                        :label="$t('domain.activity_log')"
                        icon="iris-scan-user"
                        :route="{ name: 'domain activity log', params: { domain: domainId } }"
                    />
                </div>
            </template>
        </div>
    </CFSidebar>
</template>

<style scoped>
@reference "@css/app.css";

.router-link-active {
    @apply border-0;

    span {
        @apply text-primary before:bg-primary relative z-30 mr-2 inline-flex size-8 shrink-0 items-center rounded-full bg-transparent transition-all duration-300 ease-in-out before:invisible after:bg-white;

        svg {
            @apply stroke-primary size-8 shrink-0;
        }

        &::after {
            content: '';
            @apply absolute top-1/2 left-1/2 -z-10 size-8 -translate-x-1/2 -translate-y-1/2 scale-0 rounded-full transition-all duration-200 ease-in-out;
        }
    }

    &:hover {
        span {
            @apply after:bg-primary visible text-white after:scale-100;
        }
    }
}
</style>
